/* ==========================================================================
   Button — Vapt Menu Design System
   Variants: primary, secondary, danger, ghost
   Sizes: sm, md, lg
   States: loading, disabled
   ========================================================================== */

.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  font-family: var(--font-body);
  font-weight: 500;
  line-height: 1;
  border: 2px solid transparent;
  cursor: pointer;
  transition:
    background-color var(--transition-fast),
    border-color var(--transition-fast),
    color var(--transition-fast),
    box-shadow var(--transition-fast),
    transform var(--transition-fast);
  white-space: nowrap;
  user-select: none;
  text-decoration: none;
}

.button:focus-visible {
  outline: 2px solid var(--color-focus);
  outline-offset: 2px;
}

.button:active:not(:disabled):not(.loading) {
  transform: scale(0.97);
}

/* --- Sizes --- */

.sm {
  height: 32px;
  padding: 0 var(--space-3);
  font-size: var(--text-body-sm-size);
  border-radius: var(--radius-sm);
}

.md {
  height: 40px;
  padding: 0 var(--space-4);
  font-size: var(--text-body-sm-size);
  border-radius: var(--radius-sm);
}

.lg {
  height: 48px;
  padding: 0 var(--space-6);
  font-size: var(--text-body-size);
  border-radius: var(--radius-md);
}

/*
   Alvo de toque no celular: `sm` (32px) e `md` (40px) ficavam abaixo dos 44px
   recomendados. A escala visual continua a mesma no desktop — o botao so
   ganha altura onde o dedo e o ponteiro.
   ========================================================================== */

@media (max-width: 768px) {
  .sm,
  .md {
    height: 44px;
  }
}

/* --- Variants --- */

.primary {
  background-color: var(--color-primary);
  color: var(--color-on-primary);
}

.primary:hover:not(:disabled):not(.loading) {
  background-color: var(--color-primary-hover);
}

.primary:disabled,
.primary.loading {
  background-color: var(--color-primary-light);
  color: var(--color-primary-dark);
}

.secondary {
  background-color: var(--color-surface);
  color: var(--color-text-primary);
  border-color: var(--color-border);
}

.secondary:hover:not(:disabled):not(.loading) {
  background-color: var(--color-surface-alt);
  border-color: var(--color-primary);
}

.secondary:disabled,
.secondary.loading {
  opacity: 0.5;
}

.danger {
  background-color: var(--color-error);
  color: var(--color-on-danger);
}

.danger:hover:not(:disabled):not(.loading) {
  background-color: var(--color-error-hover);
}

.danger:disabled,
.danger.loading {
  opacity: 0.5;
}

.ghost {
  background-color: transparent;
  color: var(--color-text-secondary);
}

.ghost:hover:not(:disabled):not(.loading) {
  background-color: var(--color-surface-alt);
  color: var(--color-text-primary);
}

.ghost:disabled,
.ghost.loading {
  opacity: 0.5;
}

/* --- Full width --- */

.fullWidth {
  width: 100%;
}

/* --- Loading state --- */

.loading {
  cursor: wait;
  position: relative;
}

.hiddenContent {
  visibility: hidden;
}

.spinner {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinnerIcon {
  width: 1em;
  height: 1em;
  animation: spin 600ms linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

/* --- Disabled cursor --- */

.button:disabled {
  cursor: not-allowed;
}
